import React, { useEffect, useState, Suspense } from "react";
import { Box, Typography, Button } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import HelpLinksList from "../../components/common/HelpLinksList/HelpLinksList";
import ArticlePreview from "../../components/common/ArticlePreview/ArticlePreview";
import styles from "./HelpCenter.module.css";
import articlesData from "../../help-articles/articles.json";
import * as Sentry from "@sentry/react";

const modules = import.meta.glob('../../help-articles/**/*.jsx');

const HelpCenter = () => {
  const navigate = useNavigate();
  const { category, article } = useParams();

  console.log('here');
  console.log(category);
  console.log(article);

  const [articleCategories, setArticleCategories] = useState({});
  const [popularArticles, setPopularArticles] = useState([]);
  const [ArticleComponent, setArticleComponent] = useState(null);

  useEffect(() => {
    const displayedCategories = {};
    const { settings, articles } = articlesData;

    settings.displayOrder.forEach((cat) => {
      if (articles[cat]) {
        displayedCategories[cat] = articles[cat].filter((article) => article.display);
      }
    });

    setArticleCategories(displayedCategories);
    setPopularArticles(articlesData.popularArticles);
  }, []);

  const handleLinkClick = async (path) => {
    navigate(`/help/${path}`);
    const matchedArticle = findArticleByPath(path);
    if (!matchedArticle) {
      throw new Error("Article not found in JSON");
    }

    try {
      const importPath = `../../help-articles/${matchedArticle.path}.jsx`;
      const loader = modules[importPath];
      if (!loader) throw new Error(`Module not found for path: ${importPath}`);

      const Component = React.lazy(loader);
      setArticleComponent(() => Component);
      window.scrollTo(0, 0);
    } catch (error) {
      Sentry.captureException(error);
      console.error("❌ Error loading article component:", error);
    }
  };

  const onTitleClick = (categoryName) => {
    const categoryPath = articlesData.categoryPaths[categoryName];
    if (categoryPath) {
      navigate(`/help/${categoryPath}`);
    }
  };

  const findArticleByPath = (path) => {
    const [catSegment, articleSegment] = path.split("/");
    const categoryName = Object.keys(articlesData.categoryPaths).find(
      (key) => articlesData.categoryPaths[key] === catSegment
    );
    if (!categoryName) return null;

    return articlesData.articles[categoryName]?.find((a) => a.path === path);
  };

  const baseLayout = () => (
    <Box className={styles.contentWrapper}>
      <Typography className={styles.heading}>How it works</Typography>

      <Box className={styles.links}>
        {Object.keys(articleCategories).map((cat) => (
          <HelpLinksList
            key={cat}
            title={cat}
            titleLink={cat}
            links={articleCategories[cat].map((a) => ({
              title: a.title,
              link: a.path
            }))}
            onLinkClick={(path) => handleLinkClick(path)}
            onTitleClick={onTitleClick}
          />
        ))}
      </Box>

      <Box className={styles.popularArticles}>
        <Typography className={styles.heading}>Popular help articles</Typography>
        <Box className={styles.articlesList}>
          {popularArticles.map((article, index) => (
            <ArticlePreview
              key={index}
              title={article.title}
              path={article.path}
              onReadMore={(path) => handleLinkClick(path)}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );

  const categoryLayout = () => {
    const categoryName = Object.keys(articlesData.categoryPaths).find(
      (key) => articlesData.categoryPaths[key] === category
    );

    if (!categoryName || !articleCategories[categoryName]) return <Typography>Category not found</Typography>;

    return (
      <Box className={styles.categoryLayout}>
        <Box className={styles.breadcrumb}>
          <Button onClick={() => navigate("/help")}>HOME</Button> {' > '}
          <Typography className={styles.categoryTitle}>{categoryName}</Typography>
        </Box>

        <Typography className={styles.heading}>{categoryName}</Typography>

        <Box className={styles.articlesList}>
          {articleCategories[categoryName].map((article, index) => (
            <ArticlePreview
              key={index}
              title={article.title}
              path={article.path}
              onReadMore={(path) => handleLinkClick(path)}
            />
          ))}
        </Box>
      </Box>
    );
  };

  const articleLayout = () => {
    const fullPath = `${category}/${article}`;
    const matchedArticle = findArticleByPath(fullPath);
    if (!matchedArticle) return <Typography>Article not found</Typography>;

    const categoryName = Object.keys(articlesData.categoryPaths).find(
      (key) => articlesData.categoryPaths[key] === category
    );

    const related = (articleCategories[categoryName] || [])
      .filter((a) => a.path !== fullPath)
      .slice(0, 2);

    return (
      <Box className={styles.articleLayout}>
        <Box className={styles.breadcrumb}>
          <Button onClick={() => navigate("/help")}>HOME</Button> {' > '}
          <Button onClick={() => navigate(`/help/${category}`)}>{categoryName}</Button>
        </Box>

        {ArticleComponent && (
          <Box className={styles.articleContent}>
            <Suspense fallback={<div>Loading...</div>}>
              <ArticleComponent />
            </Suspense>
          </Box>
        )}

        <Box className={styles.relatedArticles}>
          <Typography className={styles.relatedTitle}>This may also help you</Typography>
          <Box className={styles.articlesList}>
            {related.map((article, index) => (
              <ArticlePreview
                key={index}
                title={article.title}
                path={article.path}
                onReadMore={(path) => handleLinkClick(path)}
              />
            ))}
          </Box>
        </Box>
      </Box>
    );
  };

  useEffect(() => {
    if (category && article) {
      handleLinkClick(`${category}/${article}`);
    }
  }, [category, article]);

  return (
    <Box className={styles.helpCenter}>
      {!category && !article && baseLayout()}
      {category && !article && categoryLayout()}
      {category && article && articleLayout()}
    </Box>
  );
};

export default HelpCenter;